import { type ReactElement, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft';
import { type AdmissionOffer } from 'AdmissionOffer';
import { type Cohort } from 'Cohorts';
import { getIsExecEd, getSupportEmailAddress } from 'Cohorts';
import { DISCUSS_OTHER_OPTIONS, type CohortSectionOffer } from 'CohortSectionOffer';
import { isPartialScholarship } from 'Scholarship';
import { InlineLink } from 'InlineLink';
import { Radio, RadioGroup } from 'Radio';
import { Text } from 'Text';
import { TuitionPlanFrequency } from 'TuitionPlan';
import { CohortRegistrationTuitionLedger } from '../../application_status/components/CohortRegistrationTuitionLedger';
import {
    type CohortDates,
    type CohortRegistrationOptions,
    type FinancingOption,
    type PaymentOption,
    PaymentView,
} from '../TuitionAndRegistration.types';
import { RegisterButton } from './RegisterButton';
import {
    RegistrationDate,
    RegistrationDateStrip,
    RegistrationFooterColumns,
    RegistrationFooterPanel,
    RegistrationHeader,
    RegistrationGraphic,
    RegistrationNarrative,
    StartDateOptions,
} from './RegistrationLayout';
import { LOCALE_PREFIX } from '../constants';

/*
    The four blocks of the plan selection screen. They are separate components so each stays readable;
    the screen owns all of the state.
*/

type PlanSelectionIntroProps = {
    cohort: Cohort;
    admissionOffer: AdmissionOffer;
    cohortSectionOffer: CohortSectionOffer;
    dates: CohortDates | null;
    isTwoMonthExecEd: boolean;
    registerRequiresRedirectToBrowser: boolean;
};

/*
    A two-month offer with no scholarship has nothing to say above the ledger: no total to show and
    no narrative, so the ledger sits flush against the top of the card with no rule above it.
*/
export function showsRegistrationHeader({
    isTwoMonthExecEd,
    cohortSectionOffer,
}: {
    isTwoMonthExecEd: boolean;
    cohortSectionOffer: CohortSectionOffer;
}): boolean {
    return !isTwoMonthExecEd || !!cohortSectionOffer.cumulativeOfferedScholarship;
}

// The scholarship banner, the narrative copy, the tuition ledger and the deadline line.
export function PlanSelectionIntro({
    cohort,
    admissionOffer,
    cohortSectionOffer,
    dates,
    isTwoMonthExecEd,
    registerRequiresRedirectToBrowser,
}: PlanSelectionIntroProps): ReactElement {
    const { t } = useTranslation('back_royal');

    const hasPartialScholarship = isPartialScholarship(cohortSectionOffer.cumulativeOfferedScholarship);
    const hasAiLaunchScholarship =
        isTwoMonthExecEd && !!cohortSectionOffer.offeredScholarships?.some(s => s.label === 'ai_launch_2026');
    const numOfferedScholarships = cohortSectionOffer.offeredScholarships?.length ?? 0;
    const tuitionDetailsLabelKey = hasPartialScholarship
        ? `${LOCALE_PREFIX}.tuition_and_scholarship_details`
        : `${LOCALE_PREFIX}.tuition_details`;

    const showsHeader = showsRegistrationHeader({ isTwoMonthExecEd, cohortSectionOffer });

    return (
        <>
            {showsHeader ? (
                isTwoMonthExecEd ? (
                    <TwoMonthExecEdNarrative
                        cohortSectionOffer={cohortSectionOffer}
                        hasAiLaunchScholarship={hasAiLaunchScholarship}
                        tuitionDetailsLabelKey={tuitionDetailsLabelKey}
                    />
                ) : (
                    <FullNarrative
                        hasPartialScholarship={hasPartialScholarship}
                        numOfferedScholarships={numOfferedScholarships}
                        scholarshipAmount={cohortSectionOffer.cumulativeOfferedScholarship?.amount}
                        tuitionDetailsLabelKey={tuitionDetailsLabelKey}
                    />
                )
            ) : null}

            {registerRequiresRedirectToBrowser ? (
                <Text as="div" className="mt-4 text-xs text-slate-grey">
                    {t(`${LOCALE_PREFIX}.new_browser_register`)}
                </Text>
            ) : null}

            {/* The ledger and the deadline strip both span the card, past its padding. */}
            <CohortRegistrationTuitionLedger
                admissionOffer={admissionOffer}
                cohortSectionOffer={cohortSectionOffer}
                className={showsHeader ? '-mx-5 mt-5 px-5' : '-mx-5 border-t-0 px-5'}
            />

            {/* The access copy fills the strip that the deadline fills on every other offer. */}
            {isTwoMonthExecEd ? (
                <RegistrationDateStrip className="-mx-5 leading-5">
                    {hasAiLaunchScholarship ? (
                        <span>{t(`${LOCALE_PREFIX}.two_month_ai_launch_2026_deadline_text`)} </span>
                    ) : null}
                    <span>{t(`${LOCALE_PREFIX}.two_month_access_text`)}</span>
                </RegistrationDateStrip>
            ) : null}

            {!isTwoMonthExecEd && dates ? (
                <RegistrationDate
                    dates={dates}
                    isExecEd={getIsExecEd(cohort)}
                    admissionsEmail={getSupportEmailAddress(cohort, 'admissions') ?? ''}
                    className="-mx-5"
                />
            ) : null}
        </>
    );
}

// Two-month ExecEd from cycle 77 onward has no banner and no narrative copy beyond the eligibility line.
function TwoMonthExecEdNarrative({
    cohortSectionOffer,
    hasAiLaunchScholarship,
    tuitionDetailsLabelKey,
}: {
    cohortSectionOffer: CohortSectionOffer;
    hasAiLaunchScholarship: boolean;
    tuitionDetailsLabelKey: string;
}): ReactElement | null {
    const { t } = useTranslation('back_royal');

    if (!cohortSectionOffer.cumulativeOfferedScholarship) return null;

    return (
        <RegistrationNarrative tuitionDetailsLabelKey={tuitionDetailsLabelKey} numOfferedScholarships={0}>
            {hasAiLaunchScholarship ? (
                <Text as="div" className="text-center text-sm text-slate-grey sm:text-start">
                    {t(`${LOCALE_PREFIX}.two_month_ai_launch_2026_eligibility_text`)}
                </Text>
            ) : null}
        </RegistrationNarrative>
    );
}

function FullNarrative({
    hasPartialScholarship,
    numOfferedScholarships,
    scholarshipAmount,
    tuitionDetailsLabelKey,
}: {
    hasPartialScholarship: boolean;
    numOfferedScholarships: number;
    scholarshipAmount: number | null | undefined;
    tuitionDetailsLabelKey: string;
}): ReactElement {
    const scholarshipSuffix = hasPartialScholarship ? 'partial_scholarship' : 'no_scholarship';

    return (
        <RegistrationHeader>
            <RegistrationGraphic
                titleKey={`${LOCALE_PREFIX}.graphic_${scholarshipSuffix}_title`}
                subtitleKey={`${LOCALE_PREFIX}.graphic_${scholarshipSuffix}_subtitle`}
                scholarshipAmount={scholarshipAmount}
                numOfferedScholarships={numOfferedScholarships}
            />
            <RegistrationNarrative
                tuitionDetailsLabelKey={tuitionDetailsLabelKey}
                narrativeKey={`${LOCALE_PREFIX}.narrative_${scholarshipSuffix}`}
                numOfferedScholarships={numOfferedScholarships}
            />
        </RegistrationHeader>
    );
}

type PlanSelectionFooterProps = {
    activePaymentView: PaymentView;
    paymentOptions: PaymentOption[];
    selectedPaymentOptionId: string;
    selectedFinancingOption?: FinancingOption;
    registrationOptions: CohortRegistrationOptions;
    isDisabled: boolean;
    onExplainOptions: () => void;
    processingRegistration: boolean;
    registerRequiresRedirectToBrowser: boolean;
    onBack: () => void;
    onRegister: () => void;
};

/*
    The grey panel at the foot of the card, which holds whichever view the learner is on plus the
    register button. It bleeds past the card padding.
*/
export function PlanSelectionFooter(props: PlanSelectionFooterProps): ReactElement {
    return <RegistrationFooterPanel>{footerViewFor(props)}</RegistrationFooterPanel>;
}

function footerViewFor({
    activePaymentView,
    paymentOptions,
    selectedPaymentOptionId,
    selectedFinancingOption,
    registrationOptions,
    isDisabled,
    onExplainOptions,
    processingRegistration,
    registerRequiresRedirectToBrowser,
    onBack,
    onRegister,
}: PlanSelectionFooterProps): ReactElement {
    // The register button and the back link. Every view shows one of them.
    const actions = (
        <PlanSelectionActions
            activePaymentView={activePaymentView}
            multiplePlans={paymentOptions.length > 1}
            isDisabled={isDisabled}
            processingRegistration={processingRegistration}
            registerRequiresRedirectToBrowser={registerRequiresRedirectToBrowser}
            additionalFinancingOptionIsSelected={!!selectedFinancingOption}
            onBack={onBack}
            onRegister={onRegister}
        />
    );

    if (activePaymentView === PaymentView.select_plan) {
        return (
            <PlanChoices
                paymentOptions={paymentOptions}
                registrationOptions={registrationOptions}
                // Only the button waits for registration to open, so the learner can compare plans first.
                isDisabled={processingRegistration}
                onExplainOptions={onExplainOptions}
                actions={actions}
            />
        );
    }

    return (
        <>
            <FinancingOptionSelectedMessage
                selectedDiscussOtherOptions={selectedPaymentOptionId === DISCUSS_OTHER_OPTIONS}
                numIntervals={selectedFinancingOption?.additionalFinancingOption.monthlyDuration}
            />
            {actions}
        </>
    );
}

type PlanChoicesProps = {
    paymentOptions: PaymentOption[];
    registrationOptions: CohortRegistrationOptions;
    isDisabled: boolean;
    onExplainOptions: () => void;
    actions: ReactNode;
};

// The plan radios, plus the start-date radios when the offer allows other cohorts.
function PlanChoices({
    paymentOptions,
    registrationOptions,
    isDisabled,
    onExplainOptions,
    actions,
}: PlanChoicesProps): ReactElement {
    const { t } = useTranslation('back_royal');
    const multiplePlans = paymentOptions.length > 1;

    /*
        The start dates share the right column with the register button, above it.
    */
    return (
        <RegistrationFooterColumns
            actions={
                <>
                    {registrationOptions.showMultipleCohortRegistrationOptions ? (
                        <div className="max-sm:order-first">
                            <StartDateOptions
                                options={registrationOptions.cohortRegistrationOptions}
                                isDisabled={isDisabled}
                            />
                        </div>
                    ) : null}
                    {actions}
                </>
            }
        >
            <div>
                {multiplePlans ? (
                    <Text as="div" weight="semibold" className="mb-3 text-xs text-grey-darker">
                        {t(`${LOCALE_PREFIX}.choose_your_plan`)}{' '}
                        <span className="font-normal">
                            (
                            <InlineLink className="text-xs text-grey-darker" onPress={onExplainOptions}>
                                {t(`${LOCALE_PREFIX}.explain_the_options`)}
                            </InlineLink>
                            )
                        </span>
                    </Text>
                ) : null}

                <RadioGroup name="selectedPaymentOptionId" color="success" isDisabled={isDisabled}>
                    {paymentOptions.map(option => (
                        // items-start so the control lines up with the plan name, not the middle of
                        // the two-line label.
                        <Radio key={option.optionId} value={option.optionId} className="items-start">
                            <PlanOptionLabel option={option} multiplePlans={multiplePlans} />
                        </Radio>
                    ))}
                </RadioGroup>
            </div>
        </RegistrationFooterColumns>
    );
}

function PlanOptionLabel({ option, multiplePlans }: { option: PaymentOption; multiplePlans: boolean }): ReactElement {
    const { t } = useTranslation('back_royal');

    /*
        When upfront is the only plan, the discount line shows without its parenthetical.
    */
    const showsDiscountWithoutParenthetical =
        !multiplePlans &&
        'tuitionPlan' in option &&
        option.tuitionPlan.frequency === TuitionPlanFrequency.Once &&
        option.paymentInformation.netDiscountAmount > 0;

    return (
        <span className="flex flex-col">
            {/*
                leading-5 matches the 20px control box, so the top-aligned control centres on this
                line. The label inherits a 24px line box otherwise, which leaves it 2px high.
            */}
            <span className="text-xs font-medium leading-5 text-grey-darker">
                {t(option.descKey, option.descValues)}
            </span>
            {/* The details lines carry a <br />, so they need Trans rather than t(). */}
            {multiplePlans && option.detailsKey ? (
                <span className="text-2xs leading-4 text-slate-grey-500">
                    <Trans t={t} i18nKey={option.detailsKey} values={option.detailsValues} />
                </span>
            ) : null}
            {showsDiscountWithoutParenthetical ? (
                <span className="text-2xs leading-4 text-slate-grey-500">
                    <Trans
                        t={t}
                        i18nKey={`${LOCALE_PREFIX}.tuition_plan_detail_once_no_parenthetical`}
                        values={{ netDiscountAmount: option.paymentInformation.formattedNetDiscountAmount }}
                    />
                </span>
            ) : null}
        </span>
    );
}

// The dead end the learner reaches after choosing third-party financing.
function FinancingOptionSelectedMessage({
    selectedDiscussOtherOptions,
    numIntervals,
}: {
    selectedDiscussOtherOptions: boolean;
    numIntervals?: number;
}): ReactElement {
    const { t } = useTranslation('back_royal');

    return (
        <div className="mt-8">
            <Text as="h3" variant="h3" className="mb-2 text-center sm:text-start">
                {t(`${LOCALE_PREFIX}.financing_option_selected_header`)}
            </Text>
            <Text as="p" className="text-center text-sm text-slate-grey sm:text-start">
                <Trans
                    t={t}
                    i18nKey={`${LOCALE_PREFIX}.financing_option_selected_text${selectedDiscussOtherOptions ? '_other' : ''}`}
                    values={{ numIntervals }}
                    components={{
                        scheduleLink: (
                            <InlineLink
                                className="text-sm"
                                href="https://calendly.com/quantic-edu/student-financial-services-quantic-valar"
                            />
                        ),
                    }}
                />
            </Text>
        </div>
    );
}

type PlanSelectionActionsProps = {
    activePaymentView: PaymentView;
    multiplePlans: boolean;
    isDisabled: boolean;
    processingRegistration: boolean;
    registerRequiresRedirectToBrowser: boolean;
    additionalFinancingOptionIsSelected: boolean;
    onBack: () => void;
    onRegister: () => void;
};

// The register button on the plan view, and the back link on the financing view.
function PlanSelectionActions({
    activePaymentView,
    multiplePlans,
    isDisabled,
    processingRegistration,
    registerRequiresRedirectToBrowser,
    additionalFinancingOptionIsSelected,
    onBack,
    onRegister,
}: PlanSelectionActionsProps): ReactElement {
    const { t } = useTranslation('back_royal');
    const onSelectPlanView = activePaymentView === PaymentView.select_plan;

    // On the plan view this sits in the right column. The financing view has only the back link.
    const layout = onSelectPlanView ? 'flex-col' : 'flex-col items-center';

    return (
        <div className={`flex gap-3 ${layout}`}>
            {/*
                A text link rather than a Button: Button's only borderless variant is `light`, which
                fills on hover. InlineLink underlines and leaves the background alone. The arrow is a
                sibling because an InlineLink takes no icon, and only the label is underlined.
            */}
            {!onSelectPlanView ? (
                <span className="flex items-center gap-2 text-xs text-grey-darker">
                    <FontAwesomeIcon icon={faArrowLeft} />
                    <InlineLink className="text-xs text-grey-darker" onPress={onBack}>
                        {t(multiplePlans ? `${LOCALE_PREFIX}.back_to_plan_selection` : `${LOCALE_PREFIX}.back`)}
                    </InlineLink>
                </span>
            ) : null}

            {onSelectPlanView ? (
                <RegisterButton
                    processingRegistration={processingRegistration}
                    registerRequiresRedirectToBrowser={registerRequiresRedirectToBrowser}
                    additionalFinancingOptionIsSelected={additionalFinancingOptionIsSelected}
                    isDisabled={isDisabled}
                    onPress={onRegister}
                    // The plan view sizes it by its column.
                    className="w-full"
                />
            ) : null}
        </div>
    );
}
